import { Component, signal } from '@angular/core';
import { SetupWizardSectionForm } from '../../../../../forms/setup-wizard/setup-wizard-section-form/setup-wizard-section-form';
import { SetupWizardSectionsTable } from './setup-wizard-sections-table/setup-wizard-sections-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	SetupWizardPayloadObjectKeys,
	SetupWizardSection,
	SetupWizardSectionFormPayload,
	UserCsvKeys,
	SetupWizardUser,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import {
	makeEnrolledColumn,
	makeHistoricalColumn,
	makeSectionsColumn,
	SetupWizardUsersTable,
} from '../setup-wizard-user-editor/setup-wizard-users-table/setup-wizard-users-table';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

@Component({
	selector: 'app-setup-wizard-section-editor',
	imports: [
		SetupWizardSectionForm,
		SetupWizardSectionsTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		SetupWizardUsersTable,
		NzButtonModule,
		NzTooltipModule,
	],
	standalone: true,
	templateUrl: './setup-wizard-section-editor.html',
	styleUrl: './setup-wizard-section-editor.scss',
})
export class SetupWizardSectionEditor {
	public edit = signal<SetupWizardSection | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;
	public selectedTabIndex = signal<number>(0);
	public userTableColumns = signal<string[]>([
		UserCsvKeys.schoolIdOrEmail,
		UserCsvKeys.group,
		UserCsvKeys.firstName,
		UserCsvKeys.lastName,
		UserCsvKeys.active,
		makeSectionsColumn().key,
		makeEnrolledColumn().key,
		makeHistoricalColumn().key,
	]);

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardSectionFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.sections, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEditUsers($event: SetupWizardSection) {
		this.onEdit($event);
		this.selectedTabIndex.set(1);
	}

	public onEdit($event: SetupWizardSection) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardSection) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No section data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.sections, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardSection) {
		if (!$event.after) {
			throw new Error('No section data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.sections, $event.after);
	}

	public async onRemove($event: SetupWizardSection) {
		if (!$event.after) {
			throw new Error('No section data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.sections, $event.after);
	}

	public async onEnroll($event: { user: SetupWizardUser; section: SetupWizardSection }) {
		await this.setupWizard.enroll($event.user, $event.section);
	}

	public async onUnenroll($event: { user: SetupWizardUser; section: SetupWizardSection }) {
		await this.setupWizard.unenroll($event.user, $event.section);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
